import { useQuery } from "@tanstack/react-query";
import {
  INDUSTRIES,
  type Industry,
  type InferredTaxonomy,
  type RoleFamily,
  type RoleFamilyLink,
  type RoleSpecialisation,
} from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { prettyId } from "./useVerticals.js";

/**
 * The taxonomy catalogue (GET /taxonomy) — industries, role families, specialisations and the
 * family↔specialisation links, mirroring useVerticals' contract for the OTHER two layers of the
 * vocabulary (docs/TAXONOMY_PROPOSAL.md, Option B).
 *
 * Industries deliberately FALL BACK to the seeded INDUSTRIES const from @talenthop/shared while
 * the request is in flight or failed: the const and the DB seed are unit-tested identical
 * (taxonomy.test.ts), so the industry picker never has to render empty. Families and
 * specialisations have no such twin in code — when they can't be loaded the role-family fields
 * simply don't render and the wizard behaves exactly as it did before the taxonomy existed.
 */
interface TaxonomyResponse {
  industries: Industry[];
  families: RoleFamily[];
  specialisations: RoleSpecialisation[];
  links: RoleFamilyLink[];
}

export interface TaxonomyResult {
  /** Active industries in display order — never empty (falls back to the shared seed). */
  industries: Industry[];
  /** Active families under one vertical, sorted for the picker. */
  allFamilies: RoleFamily[];
  familiesFor: (verticalKey: string | null | undefined) => RoleFamily[];
  /** Active specialisations LINKED to one family — the only ones a selection may carry. */
  specsFor: (verticalKey: string | null | undefined, familyKey: string | null | undefined) => RoleSpecialisation[];
  industryName: (key: string | null | undefined) => string | null;
  familyName: (verticalKey: string | null | undefined, key: string | null | undefined) => string | null;
  specName: (verticalKey: string | null | undefined, key: string | null | undefined) => string | null;
  /** True once the role-family layer is actually usable (loaded, and non-empty). */
  rolesLoaded: boolean;
  isLoading: boolean;
  isError: boolean;
}

const activeIndustries = (rows: readonly Industry[]): Industry[] =>
  rows.filter((i) => (i as Industry & { isActive?: boolean }).isActive !== false);

export function useTaxonomy(): TaxonomyResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["taxonomy"],
    queryFn: () => api.get<TaxonomyResponse>("/taxonomy"),
    // Changes about as often as the verticals do; a session-long cache is the honest trade.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const industries = activeIndustries(data?.industries ?? INDUSTRIES);
  const families = data?.families ?? [];
  const specialisations = data?.specialisations ?? [];
  const links = data?.links ?? [];

  const familiesFor = (verticalKey: string | null | undefined): RoleFamily[] =>
    verticalKey
      ? families
          .filter((f) => f.verticalKey === verticalKey && f.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      : [];

  const specsFor = (
    verticalKey: string | null | undefined,
    familyKey: string | null | undefined,
  ): RoleSpecialisation[] => {
    if (!verticalKey || !familyKey) return [];
    const linked = new Set(
      links
        .filter((l) => l.verticalKey === verticalKey && l.familyKey === familyKey)
        .map((l) => l.specKey),
    );
    return specialisations
      .filter((s) => s.verticalKey === verticalKey && s.isActive && linked.has(s.key))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  };

  return {
    industries,
    // F5 (role-first wizard): every active family across every vertical, for the "what's the
    // role called?" picker — the family a recruiter names determines the vertical, not the other
    // way round.
    allFamilies: families,
    familiesFor,
    specsFor,
    // Name resolvers return null for an absent key (caller renders nothing) and a prettified id
    // for an unknown one — a two-year-old requisition outlives any catalogue rename.
    industryName: (key) =>
      key ? (industries.find((i) => i.key === key)?.name ?? INDUSTRIES.find((i) => i.key === key)?.name ?? prettyId(key)) : null,
    familyName: (verticalKey, key) =>
      key ? (families.find((f) => f.verticalKey === verticalKey && f.key === key)?.name ?? prettyId(key)) : null,
    specName: (verticalKey, key) =>
      key ? (specialisations.find((s) => s.verticalKey === verticalKey && s.key === key)?.name ?? prettyId(key)) : null,
    rolesLoaded: families.length > 0,
    isLoading,
    isError,
  };
}

// ── Guided intake ("understanding check") ─────────────────────────────────────

/** One gap chip — a question the JD left unanswered. At most four ever arrive; `id` keys the
 *  answer in taxonomyEnrichment.gapAnswers. */
export interface IntakeGap {
  id: string;
  question: string;
  kind?: string;
}

/**
 * POST /requisitions/intake/analyse response (apps/api taxonomy.ts). `available:false` (or any
 * parse failure upstream) means the card is silently absent and the wizard is exactly yesterday's
 * manual flow — the understanding check must never become a gate. `inferenceId` is set only when
 * the analyse ran against an existing requisition; otherwise the wizard carries `inference` +
 * `jdSha256` into the create call so the API can write the inferred-vs-final row there.
 */
export interface IntakeAnalysis {
  available: boolean;
  inferenceId?: string | null;
  jdSha256?: string;
  inference?: InferredTaxonomy;
  gaps?: IntakeGap[];
  /** At most one advisory on a self-defeating ask; "Keep as-is" is always a first-class answer. */
  rethink?: { message: string } | null;
  /**
   * Which engine answered — our own classifier, or Gemini. Never shown to the recruiter; it exists
   * so the wizard can hand it back on save. Without that round trip the flywheel row records
   * 'wizard' and the two engines' correction rates (migration 0056) can never be told apart.
   */
  source?: "classifier" | "gemini";
}
